/** Pay preview — estimated pay, salary-permission roles only (spec §4.6; formula is a setting until payroll confirms). */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TS_STATUS, api, employeeName, errorText, label, thisMonth } from './shared';

// a salary missing from the profile is null: shown as "—", never as 0
const money = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function PayTab({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await api(token).get('/pay-preview', { month })); } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, [token, month]);
  useEffect(() => { load(); }, [load]);

  const total = (data?.items || []).reduce((a, r) => a + (r.pay?.estimated_total || 0), 0);
  return (
    <div className="space-y-4 mt-4" data-testid="attendance-pay">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
        {data?.period && <span className="text-sm text-slate-600">{t('Pay period', 'فترة الراتب')} {data.period.start} – {data.period.end}</span>}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md p-3">
        <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
        <span>
          {t('Estimate only. The hourly-rate formula is a setting until the payroll advisor confirms it: ',
            'تقدير فقط. معادلة أجر الساعة إعداد قابل للتعديل حتى يؤكدها مستشار الرواتب: ')}
          <code className="text-xs">{data?.items?.[0]?.pay?.formula}</code>
        </span>
      </div>
      {error && <div className="text-sm text-rose-600">{error}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Employee', 'الموظف')}</TableHead>
            <TableHead>{t('Timesheet', 'الكشف')}</TableHead>
            <TableHead>{t('Basic', 'الأساسي')}</TableHead>
            <TableHead>{t('Allowances', 'البدلات')}</TableHead>
            <TableHead>{t('Payable OT (h)', 'إضافي مستحق (س)')}</TableHead>
            <TableHead>{t('Overtime pay', 'أجر الإضافي')}</TableHead>
            <TableHead>{t('Shortfall deduction', 'خصم النقص')}</TableHead>
            <TableHead>{t('Absence deduction', 'خصم الغياب')}</TableHead>
            <TableHead>{t('Estimated total', 'الإجمالي التقديري')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.items || []).map((r) => (
            <TableRow key={r.employee.id}>
              <TableCell>{employeeName(r.employee, isRTL)}{r.open_questions > 0 && <span className="text-amber-700 text-xs ms-1">({r.open_questions} {t('open', 'مفتوح')})</span>}</TableCell>
              <TableCell>{label(TS_STATUS, r.status, isRTL)}</TableCell>
              <TableCell>{money(r.pay.basic_salary)}</TableCell>
              <TableCell>{money(r.pay.allowances)}</TableCell>
              <TableCell>{r.pay.payable_overtime_hours}</TableCell>
              <TableCell>{money(r.pay.overtime_pay)}</TableCell>
              <TableCell className={r.pay.shortfall_deduction ? 'text-rose-700' : ''}>{money(r.pay.shortfall_deduction)}</TableCell>
              <TableCell className={r.pay.absence_deduction ? 'text-rose-700' : ''}>{money(r.pay.absence_deduction)}</TableCell>
              <TableCell className="font-semibold">
                {money(r.pay.estimated_total)}
                {r.pay.missing?.includes('compensation.basic_salary') && (
                  <span className="text-[10px] text-amber-700 ms-1">{t('salary missing in profile', 'الراتب غير موجود في الملف')}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
          {data && (
            <TableRow>
              <TableCell colSpan={8} className="font-semibold">{t('Total', 'الإجمالي')}</TableCell>
              <TableCell className="font-bold">{money(total)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
